// /mobile/screens/AddPesticide.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Alert,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';

const CATEGORY_PRESETS = ['Pupuk', 'Herbisida', 'Insektisida', 'Fungisida', 'Moluskisida'];
const COMPANY_PRESETS  = ['Pupuk Indonesia', 'Petrokimia', 'Syngenta', 'Bayer', 'Corteva', 'BASF'];
const UNIT_PRESETS     = ['kg', 'sak', 'liter', 'botol', 'ml', 'gram'];
const formatIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

const cleanDecimalText = (v) => {
  const raw = String(v ?? '').replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const parts = raw.split('.');
  if (parts.length <= 2) return raw;
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const Chip = React.memo(function Chip({ label, active, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.9}
      style={[styles.chip, active && styles.chipActive]}
    >
      <Text style={[styles.chipText, active && styles.chipTextActive]} numberOfLines={1}>
        {label}
      </Text>
    </TouchableOpacity>
  );
});

const FormInput = React.memo(function FormInput({
  label,
  placeholder,
  value = '',
  onChangeText = () => {},
  keyboardType,
  error,
  helper,
}) {
  return (
    <View style={{ marginBottom: 12 }}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        placeholder={placeholder}
        value={value ?? ''}
        onChangeText={onChangeText}
        keyboardType={keyboardType}
        autoCapitalize="none"
        autoCorrect={false}
        blurOnSubmit={false}
        style={[styles.input, !!error && styles.inputError]}
      />
      {helper ? <Text style={styles.helper}>{helper}</Text> : null}
      {!!error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
});

const PillButton = React.memo(function PillButton({ title, icon, onPress, type = 'primary', disabled }) {
  const primary = type === 'primary';
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.9}
      style={[
        styles.pill,
        primary ? styles.pillPrimary : styles.pillOutline,
        disabled && { opacity: 0.6 }
      ]}
    >
      {icon ? <Ionicons name={icon} size={16} color={primary ? '#fff' : PADIPRO.primary} style={{ marginRight: 6 }} /> : null}
      <Text style={[styles.pillText, primary ? { color: '#fff' } : { color: PADIPRO.primary }]}>{title}</Text>
    </TouchableOpacity>
  );
});

export default function AddPesticide({ route, navigation }) {
  const { token } = route.params || {};

  // ⬇️ HAPUS state name; ganti dengan ref biar kebal remount/reset
  const nameRef = useRef('');
  const [nameQuery, setNameQuery] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [searchingName, setSearchingName] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);

  const [stock, setStock] = useState('0');
  const [unitPrice, setUnitPrice] = useState('');
  const [unit, setUnit] = useState('botol');
  const [category, setCategory] = useState('Insektisida');
  const [company, setCompany] = useState('Bayer');
  const [expiredDate, setExpiredDate] = useState(''); // YYYY-MM-DD
  const [loading, setLoading] = useState(false);

  const isValidDate = (v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v);
  const stockNumber = useMemo(() => Number(stock), [stock]);
  const unitPriceNumber = useMemo(() => Number(unitPrice), [unitPrice]);
  const stockValue = useMemo(() => {
    if (!Number.isFinite(stockNumber) || !Number.isFinite(unitPriceNumber)) return 0;
    return stockNumber * unitPriceNumber;
  }, [stockNumber, unitPriceNumber]);

  const dateOk = isValidDate(expiredDate);
  const stockOk = Number.isFinite(stockNumber) && stockNumber >= 0;
  const priceOk = unitPrice === '' || (Number.isFinite(unitPriceNumber) && unitPriceNumber > 0);
  const canSubmit = !!nameRef.current.trim() && dateOk && stockOk && priceOk && !loading;

  useEffect(() => {
    const q = String(nameQuery || '').trim();
    setSelectedExisting((cur) => {
      if (!cur) return cur;
      return String(cur.name || '').trim().toLowerCase() === q.toLowerCase() ? cur : null;
    });

    if (q.length < 2 || !token) {
      setNameSuggestions([]);
      setSearchingName(false);
      return undefined;
    }

    let alive = true;
    const t = setTimeout(async () => {
      try {
        if (alive) setSearchingName(true);
        const r = await axios.get(`${API_BASE}/pesticides/search`, {
          params: { q, limit: 8, season_id: 'all' },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        const rows = Array.isArray(r.data?.items) ? r.data.items : [];
        setNameSuggestions(rows);
      } catch (e) {
        if (alive) setNameSuggestions([]);
      } finally {
        if (alive) setSearchingName(false);
      }
    }, 220);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [nameQuery, token]);

  const applyExistingProduct = (item) => {
    const name = String(item?.name || '').trim();
    if (!name) return;
    nameRef.current = name;
    setNameQuery(name);
    setSelectedExisting(item);
    setNameSuggestions([]);
    if (item.unit) setUnit(String(item.unit));
    if (item.category) setCategory(String(item.category));
    if (item.company) setCompany(String(item.company));
    if (item.unit_price != null && Number(item.unit_price) > 0) setUnitPrice(String(Number(item.unit_price)));
  };

  const submit = async () => {
    const nama = nameRef.current.trim();

    if (!nama)      return Alert.alert('Validasi', 'Nama wajib diisi');
    if (!dateOk)    return Alert.alert('Validasi', 'Tanggal harus YYYY-MM-DD (contoh: 2026-12-31)');
    if (!stockOk)   return Alert.alert('Validasi', 'Stock harus angka >= 0');
    if (!priceOk)   return Alert.alert('Validasi', 'Harga per unit harus angka > 0 atau kosongkan dulu');
    if (!token)     return Alert.alert('Error', 'Token tidak ditemukan (pastikan login sebagai admin)');

    setLoading(true);
    const payload = {
      name: nama,
      stock: stockNumber,
      unit: unit?.trim() || 'unit',
      unit_price: Number.isFinite(unitPriceNumber) && unitPriceNumber > 0 ? unitPriceNumber : null,
      category: category?.trim() || null,
      company: company?.trim() || null,
      expired_date: expiredDate?.trim() ? expiredDate.trim() : null,
    };

    try {
      await axios.post(`${API_BASE}/pesticides`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Alert.alert('Sukses', 'Produk inventori berhasil dibuat');
      navigation.goBack();
    } catch (err) {
      Alert.alert('Error', err?.response?.data?.error || err?.message || 'Gagal membuat data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: PADIPRO.bg }}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          contentContainerStyle={styles.wrap}
          keyboardShouldPersistTaps="handled"
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
        >
          <View style={{ marginBottom: 12 }}>
            <EnterpriseHeader
              title="Tambah Sarana Produksi"
              subtitle="Daftarkan pupuk, pestisida, dan stok gudang musim aktif"
              right={(
                <TouchableOpacity
                  onPress={() => navigation.goBack()}
                  style={styles.headerActionBtn}
                  activeOpacity={0.85}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="close" size={16} color="#F8FFF8" />
                </TouchableOpacity>
              )}
            />
          </View>

          {/* Card — Info utama */}
          <View style={styles.card}>
            {/* Nama produk — state tetap ringan agar input tidak terasa tersendat. */}
            <View style={{ marginBottom: 12 }}>
              <Text style={styles.label}>Nama Produk</Text>
              <TextInput
                placeholder="contoh: Urea / Phonska / Decis 25 EC"
                value={nameQuery}
                onChangeText={(t) => {
                  nameRef.current = t;
                  setNameQuery(t);
                }}
                autoCapitalize="words"
                autoCorrect={false}
                autoComplete="off"
                importantForAutofill="no"
                blurOnSubmit={false}
                maxLength={120}
                style={styles.input}
              />
              {!!selectedExisting && (
                <View style={styles.selectedProductPill}>
                  <Ionicons name="checkmark-circle" size={14} color={PADIPRO.primary} />
                  <Text style={styles.selectedProductText} numberOfLines={1}>
                    Pakai data existing: {selectedExisting.name}
                  </Text>
                </View>
              )}
              {nameSuggestions.length > 0 && (
                <View style={styles.suggestionBox}>
                  <Text style={styles.suggestionTitle}>
                    {searchingName ? 'Mencari produk...' : 'Pilih dari data existing'}
                  </Text>
                  {nameSuggestions.map((it) => (
                    <TouchableOpacity
                      key={`product-suggest-${it.pesticide_id}`}
                      style={styles.suggestionItem}
                      activeOpacity={0.88}
                      onPress={() => applyExistingProduct(it)}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.suggestionName} numberOfLines={1}>{it.name}</Text>
                        <Text style={styles.suggestionMeta} numberOfLines={1}>
                          {[it.category, it.company, it.unit].filter(Boolean).join(' • ') || 'Data lama'}
                        </Text>
                      </View>
                      <Text style={styles.suggestionPrice}>
                        {Number(it.unit_price || 0) > 0 ? formatIDR(it.unit_price) : 'No price'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
              <Text style={styles.helper}>
                Pilih nama existing kalau ada. Stok baru tetap masuk ke musim aktif, hanya nama/unit/kategori/harga yang diambil dari data lama.
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <FormInput
                  label="Stock (angka)"
                  placeholder="0"
                  value={stock}
                  onChangeText={(t) => setStock(cleanDecimalText(t))}
                  keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
                  error={!stockOk ? 'Stock harus angka ≥ 0' : ''}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Unit</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                  {UNIT_PRESETS.slice(0,3).map(u => (
                    <Chip key={u} label={u} active={unit === u} onPress={() => setUnit(u)} />
                  ))}
                </View>
                <TextInput
                  placeholder="mis. botol / liter / ml"
                  value={unit}
                  onChangeText={setUnit}
                  style={styles.input}
                />
              </View>
            </View>

            <FormInput
              label="Harga per Unit"
              placeholder="contoh: 75000"
              value={unitPrice}
              onChangeText={(t) => setUnitPrice(cleanDecimalText(t))}
              keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
              error={!priceOk ? 'Harga harus angka > 0 atau kosongkan dulu' : ''}
              helper={unitPrice
                ? `Dipakai untuk hitung biaya lahan. Nilai stok: ${formatIDR(stockValue)}`
                : 'Boleh dikosongkan dulu. Nanti biaya lahan belum bisa dihitung sampai harga diisi.'
              }
            />

            <FormInput
              label="Tanggal Kadaluarsa (opsional)"
              placeholder="YYYY-MM-DD (contoh: 2026-12-31)"
              value={expiredDate}
              onChangeText={setExpiredDate}
              error={!dateOk ? 'Format harus YYYY-MM-DD' : ''}
            />
          </View>

          {/* Card — Kategori & Perusahaan */}
          <View style={styles.card}>
            <Text style={styles.blockTitle}>Kategori</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              {CATEGORY_PRESETS.map((it) => (
                <Chip key={it} label={it} active={category === it} onPress={() => setCategory(it)} />
              ))}
            </View>
            <TextInput
              placeholder="Tulis manual jika tidak ada di atas"
              value={category}
              onChangeText={setCategory}
              style={styles.input}
            />

            <Text style={[styles.blockTitle, { marginTop: 10 }]}>Perusahaan</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              {COMPANY_PRESETS.map((it) => (
                <Chip key={it} label={it} active={company === it} onPress={() => setCompany(it)} />
              ))}
            </View>
            <TextInput
              placeholder="Tulis manual jika tidak ada di atas"
              value={company}
              onChangeText={setCompany}
              style={styles.input}
            />
          </View>

          {/* Actions */}
          <View style={styles.actionsRow}>
            <PillButton
              title={loading ? 'Processing…' : 'Create'}
              icon="checkmark-circle"
              onPress={submit}
              type="primary"
              disabled={!canSubmit}
            />
            <PillButton
              title="Cancel"
              icon="close-circle"
              onPress={() => navigation.goBack()}
              type="outline"
            />
          </View>

          {/* Tips */}
          <View style={styles.tipsBox}>
            <Ionicons name="information-circle-outline" size={16} color={PADIPRO.muted} style={{ marginRight: 6 }} />
            <Text style={styles.tipsText}>
              QR & Barcode akan dibuat otomatis. Cocok untuk pupuk dan pestisida, lalu biaya lahan dibuat saat barang dipakai.
            </Text>
          </View>

        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  wrap: { padding: 16, paddingBottom: 26 },
  headerActionBtn: {
    padding: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.32)',
  },

  card: {
    backgroundColor: PADIPRO.surface,
    borderRadius: 14,
    borderWidth: 1, borderColor: PADIPRO.border,
    padding: 14, marginBottom: 12,
    ...padiproShadow,
  },

  label: { fontWeight: '700', color: PADIPRO.text, marginBottom: 6 },
  input: {
    borderWidth: 1, borderColor: PADIPRO.border, borderRadius: 10, padding: 12,
    backgroundColor: PADIPRO.surface,
    color: PADIPRO.text,
  },
  inputError: { borderColor: PADIPRO.danger },
  helper: { color: PADIPRO.muted, fontSize: 12, marginTop: 6 },
  errorText: { color: PADIPRO.danger, fontSize: 12, marginTop: 6 },
  selectedProductPill: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  selectedProductText: {
    flex: 1,
    color: PADIPRO.primary,
    fontWeight: '800',
    fontSize: 12,
  },
  suggestionBox: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 14,
    overflow: 'hidden',
  },
  suggestionTitle: {
    paddingHorizontal: 10,
    paddingTop: 9,
    paddingBottom: 6,
    color: PADIPRO.muted,
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.border,
  },
  suggestionName: {
    color: PADIPRO.text,
    fontWeight: '900',
    fontSize: 13,
  },
  suggestionMeta: {
    marginTop: 2,
    color: PADIPRO.muted,
    fontWeight: '700',
    fontSize: 11,
  },
  suggestionPrice: {
    color: PADIPRO.primaryDark,
    fontWeight: '900',
    fontSize: 11,
    textAlign: 'right',
  },

  blockTitle: { fontWeight: '700', color: PADIPRO.text, marginBottom: 6 },

  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 },
  chip: {
    borderWidth: 1, borderColor: PADIPRO.border, paddingVertical: 8, paddingHorizontal: 12,
    borderRadius: 16, backgroundColor: PADIPRO.surface
  },
  chipActive: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.primarySoft },
  chipText: { color: PADIPRO.muted, fontWeight: '600' },
  chipTextActive: { color: PADIPRO.primary },

  actionsRow: { flexDirection: 'row', gap: 12, marginTop: 4, marginBottom: 8 },
  pill: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderRadius: 999, paddingVertical: 12, paddingHorizontal: 16,
  },
  pillPrimary: { backgroundColor: PADIPRO.primary },
  pillOutline: { backgroundColor: PADIPRO.surface, borderWidth: 1, borderColor: PADIPRO.primary },
  pillText: { fontWeight: '800', color: '#fff' },

  tipsBox: {
    flexDirection: 'row', alignItems: 'flex-start',
    backgroundColor: PADIPRO.surfaceSunken, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: PADIPRO.border
  },
  tipsText: { color: PADIPRO.muted, flex: 1, lineHeight: 18 },
});
